import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DataTable from "examples/Tables/DataTable";

export default function Empleados() {
  const [empleados, setEmpleados] = useState([]);

  useEffect(() => {
    const fetchEmpleados = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.RRHH.EMPLEADOS);
        setEmpleados(response.data);
      } catch (error) {
        console.error("Error al cargar empleados:", error);
      }
    };
    fetchEmpleados();
  }, []);

  const [openDialog, setOpenDialog] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [currentEmpleado, setCurrentEmpleado] = useState({
    nombre: "",
    numEmpleado: "",
    departamento: "",
    puesto: "",
    email: "",
    telefono: "",
    fechaIngreso: "",
    salario: "",
    estatus: "Activo",
  });

  const [filtro, setFiltro] = useState({
    departamento: "",
    estatus: "",
    busqueda: "",
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const handleOpenDialog = (empleado = null, isView = false) => {
    setViewMode(isView);
    if (empleado) {
      setCurrentEmpleado(empleado);
    } else {
      setCurrentEmpleado({
        nombre: "",
        numEmpleado: "",
        departamento: "",
        puesto: "",
        email: "",
        telefono: "",
        fechaIngreso: "",
        salario: "",
        estatus: "Activo",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setViewMode(false);
  };

  const handleSaveEmpleado = () => {
    if (currentEmpleado.id) {
      setEmpleados(empleados.map((emp) => (emp.id === currentEmpleado.id ? currentEmpleado : emp)));
    } else {
      setEmpleados([...empleados, { ...currentEmpleado, id: empleados.length + 1 }]);
    }
    handleCloseDialog();
  };

  const handleDeleteEmpleado = (id) => {
    if (window.confirm("¿Está seguro de eliminar este empleado?")) {
      setEmpleados(empleados.filter((emp) => emp.id !== id));
    }
  };

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case "Activo":
        return "success";
      case "Inactivo":
        return "error";
      case "Vacaciones":
        return "warning";
      case "Incapacidad":
        return "info";
      default:
        return "default";
    }
  };

  const empleadosFiltrados = empleados.filter((emp) => {
    const matchDept = !filtro.departamento || emp.departamento === filtro.departamento;
    const matchEstatus = !filtro.estatus || emp.estatus === filtro.estatus;
    const matchBusqueda =
      !filtro.busqueda ||
      emp.nombre.toLowerCase().includes(filtro.busqueda.toLowerCase()) ||
      emp.numEmpleado.toLowerCase().includes(filtro.busqueda.toLowerCase());
    return matchDept && matchEstatus && matchBusqueda;
  });

  const columns = [
    { Header: "No. Empleado", accessor: "numEmpleado", width: "10%" },
    { Header: "nombre", accessor: "nombre", width: "20%" },
    { Header: "departamento", accessor: "departamento", width: "15%" },
    { Header: "puesto", accessor: "puesto", width: "15%" },
    { Header: "email", accessor: "email", width: "15%" },
    { Header: "salario", accessor: "salario", width: "12%" },
    { Header: "estatus", accessor: "estatus", width: "10%" },
    { Header: "acciones", accessor: "acciones", width: "8%" },
  ];

  const rows = empleadosFiltrados.map((emp) => ({
    numEmpleado: (
      <MDTypography variant="caption" fontWeight="medium">
        {emp.numEmpleado}
      </MDTypography>
    ),
    nombre: (
      <MDBox display="flex" alignItems="center">
        <Avatar sx={{ mr: 2, bgcolor: "info.main" }}>{emp.nombre?.charAt(0) || "E"}</Avatar>
        <MDTypography variant="caption" fontWeight="medium">
          {emp.nombre}
        </MDTypography>
      </MDBox>
    ),
    departamento: <MDTypography variant="caption">{emp.departamento}</MDTypography>,
    puesto: <MDTypography variant="caption">{emp.puesto}</MDTypography>,
    email: <MDTypography variant="caption">{emp.email}</MDTypography>,
    salario: (
      <MDTypography variant="caption" fontWeight="medium" color="success">
        {formatCurrency(emp.salario)}
      </MDTypography>
    ),
    estatus: <Chip label={emp.estatus} color={getEstatusColor(emp.estatus)} size="small" />,
    acciones: (
      <MDBox display="flex" gap={1}>
        <MDButton
          variant="text"
          color="info"
          size="small"
          iconOnly
          onClick={() => handleOpenDialog(emp, true)}
        >
          <Icon>visibility</Icon>
        </MDButton>
        <MDButton
          variant="text"
          color="dark"
          size="small"
          iconOnly
          onClick={() => handleOpenDialog(emp, false)}
        >
          <Icon>edit</Icon>
        </MDButton>
        <MDButton
          variant="text"
          color="error"
          size="small"
          iconOnly
          onClick={() => handleDeleteEmpleado(emp.id)}
        >
          <Icon>delete</Icon>
        </MDButton>
      </MDBox>
    ),
  }));

  return (
    <MDBox>
      <Card>
        <MDBox p={3}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <MDTypography variant="h6" fontWeight="medium">
              Gestión de Empleados
            </MDTypography>
            <MDButton variant="gradient" color="success" onClick={() => handleOpenDialog()}>
              <Icon sx={{ fontWeight: "bold" }}>add</Icon>
              &nbsp;Nuevo Empleado
            </MDButton>
          </MDBox>

          {/* Filtros */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar empleado"
                value={filtro.busqueda}
                onChange={(e) => setFiltro({ ...filtro, busqueda: e.target.value })}
                InputProps={{
                  startAdornment: <Icon sx={{ mr: 1 }}>search</Icon>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Departamento"
                value={filtro.departamento}
                onChange={(e) => setFiltro({ ...filtro, departamento: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Todos los departamentos</option>
                <option value="Ventas">Ventas</option>
                <option value="Producción">Producción</option>
                <option value="Administración">Administración</option>
                <option value="TI">TI</option>
                <option value="RRHH">RRHH</option>
                <option value="Logística">Logística</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Estatus"
                value={filtro.estatus}
                onChange={(e) => setFiltro({ ...filtro, estatus: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Todos los estatus</option>
                <option value="Activo">Activo</option>
                <option value="Inactivo">Inactivo</option>
                <option value="Vacaciones">Vacaciones</option>
                <option value="Incapacidad">Incapacidad</option>
              </TextField>
            </Grid>
          </Grid>

          <DataTable
            table={{ columns, rows }}
            showTotalEntries={true}
            isSorted={true}
            noEndBorder
            entriesPerPage={true}
          />
        </MDBox>
      </Card>

      {/* Dialog para crear/editar/ver empleado */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewMode
            ? "Detalles del Empleado"
            : currentEmpleado.id
              ? "Editar Empleado"
              : "Nuevo Empleado"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="No. Empleado"
                value={currentEmpleado.numEmpleado}
                onChange={(e) =>
                  setCurrentEmpleado({ ...currentEmpleado, numEmpleado: e.target.value })
                }
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre Completo"
                value={currentEmpleado.nombre}
                onChange={(e) => setCurrentEmpleado({ ...currentEmpleado, nombre: e.target.value })}
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Departamento"
                value={currentEmpleado.departamento}
                onChange={(e) =>
                  setCurrentEmpleado({ ...currentEmpleado, departamento: e.target.value })
                }
                disabled={viewMode}
                SelectProps={{ native: true }}
              >
                <option value="">Seleccione</option>
                <option value="Ventas">Ventas</option>
                <option value="Producción">Producción</option>
                <option value="Administración">Administración</option>
                <option value="TI">TI</option>
                <option value="RRHH">RRHH</option>
                <option value="Logística">Logística</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Puesto"
                value={currentEmpleado.puesto}
                onChange={(e) => setCurrentEmpleado({ ...currentEmpleado, puesto: e.target.value })}
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={currentEmpleado.email}
                onChange={(e) => setCurrentEmpleado({ ...currentEmpleado, email: e.target.value })}
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={currentEmpleado.telefono}
                onChange={(e) =>
                  setCurrentEmpleado({ ...currentEmpleado, telefono: e.target.value })
                }
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de Ingreso"
                value={currentEmpleado.fechaIngreso}
                onChange={(e) =>
                  setCurrentEmpleado({ ...currentEmpleado, fechaIngreso: e.target.value })
                }
                disabled={viewMode}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Salario Mensual"
                value={currentEmpleado.salario}
                onChange={(e) =>
                  setCurrentEmpleado({ ...currentEmpleado, salario: e.target.value })
                }
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Estatus"
                value={currentEmpleado.estatus}
                onChange={(e) =>
                  setCurrentEmpleado({ ...currentEmpleado, estatus: e.target.value })
                }
                disabled={viewMode}
                SelectProps={{ native: true }}
              >
                <option value="Activo">Activo</option>
                <option value="Inactivo">Inactivo</option>
                <option value="Vacaciones">Vacaciones</option>
                <option value="Incapacidad">Incapacidad</option>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="secondary">
            {viewMode ? "Cerrar" : "Cancelar"}
          </MDButton>
          {!viewMode && (
            <MDButton onClick={handleSaveEmpleado} color="info">
              Guardar
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
